import { useCallback, useEffect, useRef, useState } from "react";

import {
  LearningEnrollmentStatus,
  ManagerLearningProgressDocument,
  ManagerLearningProgressQuery,
  ManagerLearningProgressQueryVariables,
} from "../generated/graphql";
import { client } from "../lib/apolloClient";

export type LearningProgressStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";

export type LearningProgressAttempt = {
  id: string;
  courseVersion: string;
  serverScorePercent: number;
  serverPassed: boolean;
  finishedAt: string;
};

export type LearningProgressEntry = {
  enrollmentId: string;
  organizationId: string;
  userId: string;
  courseId: string;
  status: LearningProgressStatus;
  firstStartedAt: string | null;
  completedAt: string | null;
  bestScorePercent: number | null;
  quizAttempts: LearningProgressAttempt[];
};

export type LearningProgressCourse = {
  id: string;
  title: string;
};

export type LearningProgressPath = {
  id: string;
  version: string;
  title: string;
  courses: Array<{ courseId: string; position: number }>;
};

export type LearningProgressCaregiver = {
  userId: string;
  organizationId: string;
  displayName: string;
  locationNames: string[];
};

export type LearningProgressPathAssignment = {
  id: string | null;
  userId: string;
  learningPathId: string;
  learningPathVersion: string | null;
  enrollmentIds: string[];
};

type LearningProgressFilter = ManagerLearningProgressQueryVariables["filter"];

type LearningProgressState = {
  caregivers: LearningProgressCaregiver[];
  courses: LearningProgressCourse[];
  learningPaths: LearningProgressPath[];
  pathAssignments: LearningProgressPathAssignment[];
  entries: LearningProgressEntry[];
  loading: boolean;
  error: Error | null;
};

type UseLearningProgressOptions = {
  userId?: string;
  courseId?: string;
};

const emptyData = {
  caregivers: [],
  courses: [],
  learningPaths: [],
  pathAssignments: [],
  entries: [],
};

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error("Manager learning progress request failed");

const normalizeProgressStatus = (status: LearningEnrollmentStatus): LearningProgressStatus => {
  switch (status) {
    case LearningEnrollmentStatus.NotStarted:
      return "NOT_STARTED";
    case LearningEnrollmentStatus.InProgress:
      return "IN_PROGRESS";
    case LearningEnrollmentStatus.Completed:
      return "COMPLETED";
  }
};

const mapProgress = (data: ManagerLearningProgressQuery): Omit<LearningProgressState, "loading" | "error"> => {
  const caregivers = [...data.managerLearningCaregivers].sort((left, right) =>
    left.displayName.localeCompare(right.displayName, "de-DE")
  );
  const courses = [...data.managerLearningCatalog.courses].sort((left, right) =>
    left.title.localeCompare(right.title, "de-DE")
  );
  const learningPaths = [...data.managerLearningCatalog.learningPaths]
    .map(path => ({
      ...path,
      courses: [...path.courses].sort((left, right) => left.position - right.position),
    }))
    .sort((left, right) => left.title.localeCompare(right.title, "de-DE"));
  const organizationByUserId = new Map(caregivers.map(caregiver => [caregiver.userId, caregiver.organizationId]));
  const entries = data.learningProgressReport.entries
    .filter(entry => organizationByUserId.get(entry.userId) === entry.organizationId)
    .map(entry => ({
      enrollmentId: entry.enrollmentId,
      organizationId: entry.organizationId,
      userId: entry.userId,
      courseId: entry.courseId,
      status: normalizeProgressStatus(entry.status),
      firstStartedAt: entry.firstStartedAt ?? null,
      completedAt: entry.completedAt ?? null,
      bestScorePercent: entry.bestScorePercent ?? null,
      quizAttempts: [...entry.quizAttempts]
        .sort((left, right) => new Date(left.finishedAt).getTime() - new Date(right.finishedAt).getTime())
        .map(attempt => ({
          id: attempt.id,
          courseVersion: attempt.courseVersion,
          serverScorePercent: attempt.serverScorePercent,
          serverPassed: attempt.serverPassed,
          finishedAt: attempt.finishedAt,
        })),
    }));
  const pathAssignments = data.managerLearningAssignments.flatMap(assignment => {
    if (!assignment.active || assignment.itemKind !== "LEARNING_PATH" || assignment.learningPathId === null) {
      return [];
    }
    return [
      {
        id: assignment.id ?? null,
        userId: assignment.userId,
        learningPathId: assignment.learningPathId,
        learningPathVersion: assignment.learningPathVersion ?? null,
        enrollmentIds: assignment.enrollmentIds,
      },
    ];
  });

  return { caregivers, courses, learningPaths, pathAssignments, entries };
};

export const useLearningProgress = (options: UseLearningProgressOptions = {}) => {
  const [state, setState] = useState<LearningProgressState>({
    ...emptyData,
    loading: true,
    error: null,
  });
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const userId = options.userId;
  const courseId = options.courseId;

  const load = useCallback(
    async (preserveData = false): Promise<void> => {
      const requestId = ++requestIdRef.current;
      setState(previous => ({
        ...(preserveData
          ? {
              caregivers: previous.caregivers,
              courses: previous.courses,
              learningPaths: previous.learningPaths,
              pathAssignments: previous.pathAssignments,
              entries: previous.entries,
            }
          : emptyData),
        loading: !preserveData,
        error: null,
      }));

      const filter: LearningProgressFilter = {};
      if (userId) filter.userIds = [userId];
      if (courseId) filter.courseIds = [courseId];

      try {
        const result = await client.query<ManagerLearningProgressQuery, ManagerLearningProgressQueryVariables>({
          query: ManagerLearningProgressDocument,
          variables: { filter, assignmentUserIds: userId ? [userId] : null },
          fetchPolicy: "network-only",
        });
        if (requestId !== requestIdRef.current || !mountedRef.current) return;
        setState({ ...mapProgress(result.data), loading: false, error: null });
      } catch (error) {
        if (requestId !== requestIdRef.current || !mountedRef.current) return;
        setState(previous => ({
          ...(preserveData
            ? {
                caregivers: previous.caregivers,
                courses: previous.courses,
                learningPaths: previous.learningPaths,
                pathAssignments: previous.pathAssignments,
                entries: previous.entries,
              }
            : emptyData),
          loading: false,
          error: toError(error),
        }));
      }
    },
    [courseId, userId]
  );

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, [load]);

  return {
    ...state,
    isEmpty: !state.loading && !state.error && state.entries.length === 0,
    reload: () => load(true),
  };
};
